import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik, FormikProvider } from 'formik';
import {
  AlertError,
  Button,
  Form,
  FormFieldset,
  FormikInputText,
  FormikDropdownSearch,
  FormikPhoneInput,
  isFormikValid,
  FormLabel,
  FieldError,
  Spinner,
} from '@percihealth/react';
import {
  ReferralCreateGenericSchema,
  ReferralCreateGeneric,
  joinReasons,
  ReferralType,
  generateFirebaseId,
  OrganizationId,
} from '@packages/core-shared';
import { getFetchResponseErrorMessage } from '@packages/web-shared';
import { useLoadOrganizations } from '@packages/web-shared/api';

import { DocumentUpload } from './DocumentUpload';
import { useAuth } from '@percihealth/react-auth';

const joinReasonOptions = joinReasons.map((cr) => ({
  value: cr.id,
  name: cr.value,
}));

export default function ReferralForm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [responseError, setResponseError] = useState<string | undefined>(
    undefined,
  );

  const organizationsLoader = useLoadOrganizations();
  useEffect(() => {
    organizationsLoader.load();
  }, [user]);
  const organizationOptions = useMemo(() => {
    // Show only 2 organizations for Generic form
    const orgs = organizationsLoader.records.filter((org) =>
      [
        OrganizationId.internationalpmi as string,
        OrganizationId.occhealthnow as string,
      ].includes(org.id),
    );
    orgs.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    const options = [
      { value: '', name: '-' },
      ...orgs.map((org) => ({ value: org.id, name: org.name })),
    ];

    return options;
  }, [organizationsLoader.records]);

  const [referralForms, setReferralForms] = useState<
    {
      name: string;
      path: string;
    }[]
  >([]);

  const [referralId, setReferralId] = useState(generateFirebaseId());
  useEffect(() => {
    setReferralId(generateFirebaseId());
  }, []);

  const referralHandleOnsubmit = async (values: any, actions: any) => {
    if (!isFormikValid(formik)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/referrals`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...values,
            type: ReferralType.generic,
          }),
        },
      );

      if (!response.ok) {
        const errorMsg = await getFetchResponseErrorMessage(response);
        console.error(errorMsg);
        setResponseError(errorMsg);
        return;
      }

      navigate('/referred');
    } catch (error) {
      console.error(error);
      setResponseError((error as Error).message);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: referralId,
      patient: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
      joinReasonId: joinReasons[0].id,
      referrer: {
        email: '',
        firstName: '',
        lastName: '',
      },
      organizationId: '',
      // gip: {
      //   code: '',
      // },
      documents: {
        consent: {
          name: '',
          path: '',
        },
        referrals: [],
      },
    } as ReferralCreateGeneric,
    validationSchema: ReferralCreateGenericSchema,
    onSubmit: referralHandleOnsubmit,
  });

  console.log(formik.isValid);

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <FormFieldset title="Patient's details">
          <FormikInputText
            name="patient.firstName"
            required
            label="Patient first name"
            value={formik.values.patient.firstName}
          />
          <FormikInputText
            name="patient.lastName"
            required
            label="Patient surname"
            value={formik.values.patient.lastName}
          />
          <FormikInputText
            name="patient.email"
            required
            label="Patient email address"
            value={formik.values.patient.email}
            onBlur={(e) => {
              formik.setFieldValue(
                'patient.email',
                e.target.value?.toLowerCase(),
              );
              formik.setFieldTouched(
                'patient.email',
                true /* set touched=true */,
                false /* do not validate, validation is set onChange */,
              );
            }}
          />
          <FormikPhoneInput
            name="patient.phone"
            label="Patient telephone number"
            value={formik.values.patient.phone}
          />
          <FormikDropdownSearch
            label="Join reason"
            name="joinReasonId"
            required
            multiple={false}
            value={formik.values.joinReasonId.toString()}
            options={joinReasonOptions}
          />
        </FormFieldset>
        <FormFieldset title="Referrer details">
          <FormikInputText
            id="referrer.firstName"
            name="referrer.firstName"
            required
            label="Referrer first name"
            value={formik.values.referrer.firstName}
          />
          <FormikInputText
            id="referrer.lastName"
            name="referrer.lastName"
            required
            label="Referrer surname"
            value={formik.values.referrer.lastName}
          />
          <FormikInputText
            id="referrer.email"
            name="referrer.email"
            required
            label="Referrer email address"
            value={formik.values.referrer.email}
            onBlur={(e) => {
              formik.setFieldValue(
                'referrer.email',
                e.target.value?.toLowerCase(),
              );
              formik.setFieldTouched(
                'referrer.email',
                true /* set touched=true */,
                false /* do not validate, validation is set onChange */,
              );
            }}
          />
        </FormFieldset>
        {/* <FormFieldset title="GIP">
          <FormikInputText
            id="gip.code"
            name="gip.code"
            required
            label="Group Income Protection (GIP) code"
            value={formik.values.gip.code}
          />
        </FormFieldset> */}

        <FormFieldset title="Organisation">
          <FormikDropdownSearch
            label="Organisation"
            name="organizationId"
            required
            // onChange={(value) => setCancelReasonValue(value as string)}
            options={organizationOptions}
          />
          {organizationsLoader.loading && <Spinner />}
          {organizationsLoader.errorMessage && (
            <AlertError>{organizationsLoader.errorMessage}</AlertError>
          )}
        </FormFieldset>
        <FormFieldset title="Documents">
          <div>
            <FormLabel>Referral forms (PDF, Microsoft Word, Images)</FormLabel>
            {referralForms.length > 0 && (
              <div style={{ marginLeft: '8px', marginBottom: '24px' }}>
                {referralForms.map((form) => (
                  <div
                    style={{ marginTop: '8px', display: 'flex', gap: '16px' }}
                    key={`${form.path}/${form.name}`}
                  >
                    <div style={{ wordBreak: 'break-word' }}>{form.name}</div>
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const forms = referralForms.filter(
                          (f) => !(f.path == form.path && f.name === form.name),
                        );
                        setReferralForms(forms);
                        formik.setFieldValue(
                          'documents.referrals',
                          forms,
                          true /*validate*/,
                        );
                      }}
                    >
                      <img
                        src="/img/icons/trash.svg"
                        alt="Remove"
                        style={{ width: '24px', height: '24px' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <DocumentUpload
              type="referral"
              extensions=".pdf, .doc, .docx, jpg, jpeg, png"
              referralId={referralId}
              canUpload={referralForms.length <= 20}
              onUploaded={async (e) => {
                const forms = [
                  ...referralForms,
                  { name: e.fileName, path: e.relativePath },
                ];

                setReferralForms(forms);

                await formik.setFieldValue(
                  'documents.referrals',
                  forms,
                  true /*validate*/,
                );
              }}
            />

            {formik?.errors?.documents?.referrals && formik.submitCount > 0 && (
              <FieldError style={{ position: 'initial' }}>
                {formik?.errors?.documents?.referrals}
              </FieldError>
            )}
          </div>

          <div>
            <FormLabel required>Consent form (PDF, Word Document)</FormLabel>
            {formik?.values?.documents?.consent?.name && (
              <div
                style={{
                  marginTop: '8px',
                  marginBottom: '24px',
                  wordBreak: 'break-word',
                }}
              >
                {formik?.values?.documents?.consent?.name}
              </div>
            )}
            {formik?.errors?.documents?.consent?.name &&
              formik.submitCount > 0 && (
                <FieldError style={{ position: 'initial' }}>
                  {formik?.errors?.documents?.consent?.name}
                </FieldError>
              )}
            <DocumentUpload
              type="consent"
              extensions=".pdf,.doc,.docx"
              referralId={referralId}
              onUploaded={async (e) => {
                await formik.setFieldValue(
                  'documents.consent.name',
                  e.fileName,
                  true /*validate*/,
                );
                await formik.setFieldValue(
                  'documents.consent.path',
                  e.relativePath,
                  true /*validate*/,
                );
              }}
            />
          </div>
        </FormFieldset>

        <FormFieldset title="">
          <div>
            <Button type="submit" fullWidth submitting={formik.isSubmitting}>
              Submit
            </Button>
          </div>
          <div className="full-width">
            {responseError && <AlertError>{responseError}</AlertError>}
          </div>
        </FormFieldset>
      </Form>
    </FormikProvider>
  );
}
